/**
 * WordPress dependencies
 */
import { _x } from '@safe-wordpress/i18n';

/**
 * External dependencies
 */
import { last, uniq } from 'lodash';
import { hasHead } from './functions';

export function getElementLabel( selector: string ): string {
	if ( ! selector ) {
		return LABELS.Element;
	}

	if ( 0 <= selector.indexOf( ',' ) ) {
		const labels = uniq( selector.split( ',' ).map( getElementLabel ) );
		return hasHead( labels ) ? labels[ 0 ] : LABELS.Element;
	}

	selector = cleanCssSelector( selector );
	if ( selector === '*' ) {
		return LABELS.AllElements;
	}

	if ( selector === ':root' ) {
		return LABELS.Root;
	}

	let lastSelector = last( getSelectorArray( selector ) ) ?? '';
	lastSelector = lastSelector.trim();
	if ( lastSelector.includes( '.' ) ) {
		lastSelector = '.' + lastSelector.split( '.' )[ 1 ];
	}
	if ( lastSelector.includes( '#' ) ) {
		lastSelector = '#' + lastSelector.split( '#' )[ 1 ];
	}
	if ( lastSelector.includes( ':' ) ) {
		lastSelector = lastSelector.split( ':' )[ 0 ] as string;
	}

	const tagName: string = getTagName( lastSelector ).toUpperCase();
	const elementId: string = getElementId( lastSelector ).toUpperCase();
	const className: string = getClassName( lastSelector ).toUpperCase();

	// eslint-disable-next-line @wordpress/no-unused-vars-before-return
	const cleanedClassName = `${ lastSelector } ${ elementId } ${ className }`
		.replace( /-/g, ' ' )
		.replace( /\s+/g, ' ' )
		.replace( /[^a-zA-Z0-9\s]/g, '' )
		.trim();

	if ( tagName === 'BODY' ) {
		return LABELS.Body;
	}
	if ( /\bWIDGET\b/.test( cleanedClassName ) ) {
		return LABELS.Widget;
	}
	if ( /\b(MENU|MAIN-MENU|NAVIGATION|NAV)\b/.test( cleanedClassName ) ) {
		return LABELS.Menu;
	}
	if ( /\b(FA|FA-(.*?))\b/.test( className ) ) {
		return LABELS.FontIcon;
	}
	if ( /\bSUBMIT\b/.test( cleanedClassName ) && tagName === 'INPUT' ) {
		return LABELS.SubmitButton;
	}
	if ( /\bMENUITEM\b/.test( cleanedClassName ) ) {
		return LABELS.MenuItem;
	}
	if ( /\b(ENTRYMETA|ENTRYMETABOX|POSTMETABOX)\b/.test( cleanedClassName ) ) {
		return LABELS.PostMetaDivision;
	}
	if ( /\bCOMMENTREPLYTITLE\b/.test( cleanedClassName ) ) {
		return LABELS.CommentReplyTitle;
	}
	if ( /\bLOGGEDINAS\b/.test( cleanedClassName ) ) {
		return LABELS.LoginInfo;
	}
	if ( /\bFORMALLOWEDTAGS\b/.test( cleanedClassName ) ) {
		return LABELS.AllowedTagsSection;
	}
	if ( /\bLOGO\b/.test( cleanedClassName ) ) {
		return LABELS.Logo;
	}
	if ( /\b(ENTRYTITLE|POSTTITLE)\b/.test( cleanedClassName ) ) {
		return LABELS.PostTitle;
	}
	if ( /\bCOMMENTFORM\b/.test( cleanedClassName ) ) {
		return LABELS.CommentForm;
	}
	if ( /\bWIDGETTITLE\b/.test( cleanedClassName ) ) {
		return LABELS.WidgetTitle;
	}
	if ( /\bTAGCLOUD\b/.test( cleanedClassName ) ) {
		return LABELS.TagCloud;
	}
	if ( /\bROW\b/.test( cleanedClassName ) ) {
		return LABELS.Row;
	}
	if ( /\b(BUTTON|BTN)\b/.test( cleanedClassName ) ) {
		return LABELS.Button;
	}
	if ( /\bLEAD\b/.test( cleanedClassName ) ) {
		return LABELS.Lead;
	}
	if ( /\bWELL\b/.test( cleanedClassName ) ) {
		return LABELS.Well;
	}
	if ( /\bACCORDIONTOGGLE\b/.test( cleanedClassName ) ) {
		return LABELS.AccordionToggle;
	}
	if ( /\bPANELBODY\b/.test( cleanedClassName ) ) {
		return LABELS.AccordionContent;
	}
	if ( /\bALERT\b/.test( cleanedClassName ) ) {
		return LABELS.AlertDivision;
	}
	if ( /\bMORELINK\b/.test( cleanedClassName ) ) {
		return LABELS.ShowMoreLink;
	}
	if (
		/\b(MENULINK|MENUICON|MENUBTN|MENUBUTTON)\b/.test( cleanedClassName )
	) {
		return LABELS.MenuLink;
	}
	if ( /\bSUBMENU\b/.test( cleanedClassName ) ) {
		return LABELS.SubMenu;
	}
	if ( /\b(POSTBODY|POST)\b/.test( cleanedClassName ) ) {
		return LABELS.PostDivision;
	}
	if ( /\b(CONTENT|DEFAULTCONTENT)\b/.test( cleanedClassName ) ) {
		return LABELS.ContentDivision;
	}
	if ( /\bENTRYCONTENT\b/.test( cleanedClassName ) ) {
		return LABELS.EntryContent;
	}
	if ( /\bENTRYFOOTER\b/.test( cleanedClassName ) ) {
		return LABELS.EntryFooter;
	}
	if ( /\bENTRYHEADER\b/.test( cleanedClassName ) ) {
		return LABELS.EntryHeader;
	}
	if ( /\bENTRYTIME\b/.test( cleanedClassName ) ) {
		return LABELS.EntryTime;
	}
	if ( /\bPOSTEDITLINK\b/.test( cleanedClassName ) ) {
		return LABELS.PostEditLink;
	}
	if ( /\bPOSTTHUMBNAIL\b/.test( cleanedClassName ) ) {
		return LABELS.PostThumbnail;
	}
	if ( /\bTHUMBNAIL\b/.test( cleanedClassName ) ) {
		return LABELS.Thumbnail;
	}
	if ( /ATTACHMENT/.test( className ) ) {
		return LABELS.ThumbnailImage;
	}
	if ( /\bEDITLINK\b/.test( cleanedClassName ) ) {
		return LABELS.EditLink;
	}
	if ( /\bCOMMENTSLINK\b/.test( cleanedClassName ) ) {
		return LABELS.CommentsLinkDivision;
	}
	if ( /\bSITEDESCRIPTION\b/.test( cleanedClassName ) ) {
		return LABELS.SiteDescription;
	}

	if ( ! isUndefined( className ) ) {
		const match = className.match(
			/([a-zA-Z0-9_-]+)?(SPAN|SMALL|MEDIUM|LARGE|COL|COLUMN|[_-]L|[_-]M|[_-]S|COL-XS|COL-MD|COL-LG|-COL-SM)([_-])?[0-9]+/
		);
		if ( ! isUndefined( match ) && ! /SECTION/.test( className ) ) {
			const colNumber = parseInt(
				( match as RegExpMatchArray )[ 0 ].replace( /\D/g, '' )
			);
			if ( colNumber > 0 && colNumber < 13 ) {
				return LABELS.Column.replace( '%d', `${ colNumber }` );
			}
		}
	}

	// Process CSS class name if applicable
	const processedClassName = processCssClassName( lastSelector );
	if ( processedClassName && /(#|\.)/.test( lastSelector ) ) {
		return processedClassName;
	}

	// Check if the last selector is a known semantic HTML tag
	const generalTags: Record< string, string > = {
		H1: LABELS.Heading,
		H2: LABELS.Heading,
		H3: LABELS.Heading,
		H4: LABELS.Heading,
		H5: LABELS.Heading,
		H6: LABELS.Heading,
		P: LABELS.Paragraph,
		IMG: LABELS.Image,
		A: LABELS.Link,
		LI: LABELS.ListItem,
		UL: LABELS.List,
		HEADER: LABELS.Header,
		FOOTER: LABELS.Footer,
		ARTICLE: LABELS.Article,
		CODE: LABELS.CodeTag,
		FORM: LABELS.FormDivision,
		LABEL: LABELS.Label,
		OL: LABELS.List,
		BLOCKQUOTE: LABELS.BlockQuote,
		NAV: LABELS.Navigation,
	};

	if (
		Object.keys( generalTags ).includes( lastSelector.toUpperCase() ) &&
		! /(#|\.)/.test( lastSelector )
	) {
		return generalTags[ lastSelector.toUpperCase() ] as string;
	}

	// Check for common class-based element names
	if ( /\b(CONTAINER|WRAPPER)\b/.test( cleanedClassName ) ) {
		return LABELS.Wrapper;
	}

	// Check for tag-specific names
	const tagToElementMap: Record< string, string > = {
		BR: LABELS.LineBreak,
		HR: LABELS.HorizontalRule,
		PRE: LABELS.Preformatted,
		TABLE: LABELS.Table,
		TR: LABELS.TableRow,
		TD: LABELS.TableData,
		BUTTON: LABELS.Button,
		CENTER: LABELS.CentredBlock,
		DL: LABELS.DefinitionList,
		DT: LABELS.DefinitionTerm,
		DD: LABELS.DefinitionDescription,
		TEXTAREA: LABELS.TextArea,
		TBODY: LABELS.BodyOfTable,
		THEAD: LABELS.HeadOfTable,
		TFOOT: LABELS.FootOfTable,
		U: LABELS.UnderlineTextTag,
		Q: LABELS.Quotation,
		CITE: LABELS.CitationTag,
		TIME: LABELS.TimeTag,
		CAPTION: LABELS.CaptionOfTable,
		INPUT: LABELS.Input,
		SPAN: LABELS.SpanTag,
		B: LABELS.BoldTag,
		EM: LABELS.ItalicTag,
		I: LABELS.ItalicTag,
		STRONG: LABELS.StrongTag,
		SMALL: LABELS.SmallTag,
		DIV: LABELS.Division,
	};

	if ( tagToElementMap[ tagName ] ) {
		return tagToElementMap[ tagName ];
	}

	if ( generalTags[ tagName ] ) {
		return generalTags[ tagName ];
	}

	// Return a properly formatted tag name or "Unknown Element" if undefined
	return tagName
		? capitalizeFirstLetter(
				tagName.replace( /(_|-)/g, ' ' ).toLowerCase()
		  )
		: LABELS.Element;
}

// ====
// DATA
// ====

const LABELS = {
	AccordionContent: _x( 'Accordion Content', 'text', 'nelio-ab-testing' ),
	AccordionToggle: _x( 'Accordion Toggle', 'text', 'nelio-ab-testing' ),
	AlertDivision: _x( 'Alert Division', 'text', 'nelio-ab-testing' ),
	AllElements: _x( 'All Elements', 'text', 'nelio-ab-testing' ),
	AllowedTagsSection: _x( 'AllowedTags Section', 'text', 'nelio-ab-testing' ),
	Article: _x( 'Article', 'text', 'nelio-ab-testing' ),
	BlockQuote: _x( 'Block Quote', 'text', 'nelio-ab-testing' ),
	BodyOfTable: _x( 'Body of Table', 'text', 'nelio-ab-testing' ),
	Body: _x( 'Body', 'text', 'nelio-ab-testing' ),
	BoldTag: _x( 'Bold Tag', 'text', 'nelio-ab-testing' ),
	Button: _x( 'Button', 'text', 'nelio-ab-testing' ),
	CaptionOfTable: _x( 'Caption of table', 'text', 'nelio-ab-testing' ),
	CentredBlock: _x( 'Centred Block', 'text', 'nelio-ab-testing' ),
	CitationTag: _x( 'Citation Tag', 'text', 'nelio-ab-testing' ),
	CodeTag: _x( 'Code Tag', 'text', 'nelio-ab-testing' ),
	/* translators: %d: Is the number of the column. */
	Column: _x( 'Column %d/12', 'text', 'nelio-ab-testing' ),
	CommentForm: _x( 'Comment Form', 'text', 'nelio-ab-testing' ),
	CommentReplyTitle: _x( 'CommentReply Title', 'text', 'nelio-ab-testing' ),
	CommentsLinkDivision: _x(
		'CommentsLink Division',
		'text',
		'nelio-ab-testing'
	),
	ContentDivision: _x( 'Content Division', 'text', 'nelio-ab-testing' ),
	DefinitionDescription: _x(
		'Definition Description',
		'text',
		'nelio-ab-testing'
	),
	DefinitionList: _x( 'Definition list', 'text', 'nelio-ab-testing' ),
	DefinitionTerm: _x( 'Definition term', 'text', 'nelio-ab-testing' ),
	Division: _x( 'Division', 'text', 'nelio-ab-testing' ),
	EditLink: _x( 'Edit Link', 'text', 'nelio-ab-testing' ),
	Element: _x( 'Element', 'text', 'nelio-ab-testing' ),
	EntryContent: _x( 'Entry Content', 'text', 'nelio-ab-testing' ),
	EntryFooter: _x( 'Entry Footer', 'text', 'nelio-ab-testing' ),
	EntryHeader: _x( 'Entry Header', 'text', 'nelio-ab-testing' ),
	EntryTime: _x( 'Entry Time', 'text', 'nelio-ab-testing' ),
	FontIcon: _x( 'Font Icon', 'text', 'nelio-ab-testing' ),
	FootOfTable: _x( 'Foot of Table', 'text', 'nelio-ab-testing' ),
	Footer: _x( 'Footer', 'text', 'nelio-ab-testing' ),
	FormDivision: _x( 'Form Division', 'text', 'nelio-ab-testing' ),
	HeadOfTable: _x( 'Head of Table', 'text', 'nelio-ab-testing' ),
	Header: _x( 'Header', 'text', 'nelio-ab-testing' ),
	Heading: _x( 'Heading', 'text', 'nelio-ab-testing' ),
	HorizontalRule: _x( 'Horizontal Rule', 'text', 'nelio-ab-testing' ),
	Image: _x( 'Image', 'text', 'nelio-ab-testing' ),
	Input: _x( 'Input', 'text', 'nelio-ab-testing' ),
	ItalicTag: _x( 'Italic Tag', 'text', 'nelio-ab-testing' ),
	Label: _x( 'Label', 'text', 'nelio-ab-testing' ),
	Lead: _x( 'Lead', 'text', 'nelio-ab-testing' ),
	LineBreak: _x( 'Line Break', 'text', 'nelio-ab-testing' ),
	Link: _x( 'Link', 'text', 'nelio-ab-testing' ),
	ListItem: _x( 'List Item', 'text', 'nelio-ab-testing' ),
	List: _x( 'List', 'text', 'nelio-ab-testing' ),
	LoginInfo: _x( 'Login Info', 'text', 'nelio-ab-testing' ),
	Logo: _x( 'Logo', 'text', 'nelio-ab-testing' ),
	MenuItem: _x( 'Menu Item', 'text', 'nelio-ab-testing' ),
	MenuLink: _x( 'Menu Link', 'text', 'nelio-ab-testing' ),
	Menu: _x( 'Menu', 'text', 'nelio-ab-testing' ),
	Navigation: _x( 'Navigation', 'text', 'nelio-ab-testing' ),
	Paragraph: _x( 'Paragraph', 'text', 'nelio-ab-testing' ),
	PostDivision: _x( 'Post Division', 'text', 'nelio-ab-testing' ),
	PostEditLink: _x( 'Post Edit Link', 'text', 'nelio-ab-testing' ),
	PostMetaDivision: _x( 'PostMeta Division', 'text', 'nelio-ab-testing' ),
	PostThumbnail: _x( 'Post Thumbnail', 'text', 'nelio-ab-testing' ),
	PostTitle: _x( 'Post Title', 'text', 'nelio-ab-testing' ),
	Preformatted: _x( 'Preformatted', 'text', 'nelio-ab-testing' ),
	Quotation: _x( 'Quotation', 'text', 'nelio-ab-testing' ),
	Root: _x( 'Root', 'text', 'nelio-ab-testing' ),
	Row: _x( 'Row', 'text', 'nelio-ab-testing' ),
	ShowMoreLink: _x( 'Show More Link', 'text', 'nelio-ab-testing' ),
	SiteDescription: _x( 'Site Description', 'text', 'nelio-ab-testing' ),
	SmallTag: _x( 'Small Tag', 'text', 'nelio-ab-testing' ),
	SpanTag: _x( 'Span Tag', 'text', 'nelio-ab-testing' ),
	StrongTag: _x( 'Strong Tag', 'text', 'nelio-ab-testing' ),
	SubMenu: _x( 'Sub Menu', 'text', 'nelio-ab-testing' ),
	SubmitButton: _x( 'Submit Button', 'text', 'nelio-ab-testing' ),
	TableData: _x( 'Table Data', 'text', 'nelio-ab-testing' ),
	TableRow: _x( 'Table Row', 'text', 'nelio-ab-testing' ),
	Table: _x( 'Table', 'text', 'nelio-ab-testing' ),
	TagCloud: _x( 'Tag Cloud', 'text', 'nelio-ab-testing' ),
	TextArea: _x( 'Text Area', 'text', 'nelio-ab-testing' ),
	ThumbnailImage: _x( 'Thumbnail Image', 'text', 'nelio-ab-testing' ),
	Thumbnail: _x( 'Thumbnail', 'text', 'nelio-ab-testing' ),
	TimeTag: _x( 'Time Tag', 'text', 'nelio-ab-testing' ),
	UnderlineTextTag: _x( 'Underline Text tag', 'text', 'nelio-ab-testing' ),
	Well: _x( 'Well', 'text', 'nelio-ab-testing' ),
	WidgetTitle: _x( 'Widget Title', 'text', 'nelio-ab-testing' ),
	Widget: _x( 'Widget', 'text', 'nelio-ab-testing' ),
	Wrapper: _x( 'Wrapper', 'text', 'nelio-ab-testing' ),
};

// =======
// HELPERS
// =======

function cleanCssSelector( selector: string ): string {
	// Remove standard CSS pseudo-classes
	selector = selector.replace(
		/\:(hover|focus|link|visited|active|checked|disabled|enabled|invalid|valid)/g,
		''
	);

	// Remove CSS pseudo-elements (single or double colon)
	selector = selector.replace(
		/:(:)?(after|before|first-letter|first-line|selection)/g,
		''
	);

	// Trim and return the cleaned selector
	return selector.trim();
}

function getSelectorArray( selector: string ) {
	// Clean up selector
	selector = selector
		.trim()
		.replace( /\s\s+/g, ' ' ) // Remove extra spaces
		.replace( /(\s)?([>+~,])(\s)?/g, '$2' ) // Remove spaces around ">,+,~"
		.replace( />/g, ' ' ) // Convert '>' to space
		.trim();

	// Split by space if there are multiple selectors
	const selectorArray = selector.length
		? selector.split( ' ' ).map( ( v ) => v.trim() )
		: [];

	// Add spaces around '~' and '+' and return the result
	return selectorArray.map( ( v ) =>
		v.replace( /\~/g, ' ~ ' ).replace( /\+/g, ' + ' )
	);
}

function processCssClassName( input: string | undefined ) {
	if ( typeof input === 'undefined' ) {
		return false;
	}

	// Remove CSS class and ID prefixes
	input = input.replace( /[#.]/g, '' );

	// Convert CamelCase to kebab-case and lowercase everything
	input = input.replace( /([A-Z][a-z])/g, '-$1' ).toLowerCase();

	const hasUppercase = /[A-Z]/;
	const hasHyphen = /-/;

	if (
		hasHyphen.test( input ) &&
		( input.match( /(-|_)/g ) || [] ).length >= 5
	) {
		return false;
	}

	// Replace underscores and hyphens with spaces
	input = input.replace( /_/g, ' ' ).replace( /-/g, ' ' );

	// Final validation based on length and uppercase presence
	if ( hasUppercase.test( input ) || input.length < 3 || input.length > 50 ) {
		return false;
	}

	// Process the cleaned-up string
	return filterAndFormatString( input );
}

function filterAndFormatString( input: string ): string {
	const words = input.split( ' ' ); // Split input into an array of words
	const filteredWords: string[] = [];

	for ( const word of words ) {
		if ( word.length > 28 ) {
			continue;
		}

		// Ignore certain keywords if multiple words exist
		if (
			words.length > 1 &&
			/\b(elementor|oxy|ast|uael|owl|avia)\b/gi.test( word )
		) {
			continue;
		}

		if ( /[AEIOU]/i.test( word ) && word.length > 2 ) {
			filteredWords.push( word ); // Keep words with vowels if > 2 characters
		} else if ( ! /[A-Z]/i.test( word ) ) {
			filteredWords.push( word ); // Keep words without uppercase letters
		} else if ( word.length > 3 ) {
			filteredWords.push( word ); // Keep words longer than 3 characters
		}
	}

	const result = filteredWords.join( ' ' ).trim();

	return result.length > 2 ? capitalizeFirstLetter( result ) : '';
}

function capitalizeFirstLetter( text: string ): string {
	return text.charAt( 0 ).toUpperCase() + text.slice( 1 );
}

function isUndefined( a: unknown ): boolean {
	return (
		typeof a === typeof undefined ||
		a === false ||
		a === '' ||
		a === ' ' ||
		a === 'undefined' ||
		a === null
	);
}

function getTagName( selector: string ): string {
	const candidate = getLastElement( selector );

	if ( candidate.startsWith( '#' ) || candidate.startsWith( '.' ) ) {
		return '';
	}

	return candidate.split( /[#.]/ )[ 0 ] ?? '';
}

function getElementId( selector: string ): string {
	const candidate = getLastElement( selector );
	const matches = candidate.match( /#([^.]+)/ ) || [];
	return matches[ 1 ] || '';
}

function getClassName( selector: string ): string {
	const candidate = getLastElement( selector );
	return candidate
		.replace( /^[^#.]+/, '' )
		.replace( /#[^.]+/g, '' )
		.replace( /\./g, ' ' )
		.trim();
}

function getLastElement( selector: string ): string {
	return (
		last(
			selector
				.replace( /\s+/g, ' ' )
				.replace( /:[a-z0-9-_]+(\([^)]*\))?/g, '' )
				.split( ' ' )
		) ?? ''
	).trim();
}
